<template>
  <eg-modal
    id="add-submissions-modal"
    :title="$t('essays.addSubmissions.title')"
    :closable="true"
    :value="true"
    size="large"
    style="z-index: var(--layout-z-index-modal)"
  >
    <div v-if="isEssayGraderFolder">
      <eg-form-group
        v-if="uploadType === 'text'"
        id="name-input-form-group"
        ref="nameInput"
        :label="$t('essays.addSubmissions.nameLabel')"
        label-for="add-submission-name-input"
        required
        class="mb-3"
      >
        <eg-b-form-input
          id="add-submission-name-input"
          v-model="name"
          type="text"
          :placeholder="$t('essays.addSubmissions.namePlaceholder')"
          :state="submitted ? Boolean(name.length) : null"
        ></eg-b-form-input>
        <eg-b-form-invalid-feedback v-if="submitted && !Boolean(name.length)">
          {{ $t("essays.addSubmissions.nameRequired") }}
        </eg-b-form-invalid-feedback>
      </eg-form-group>

      <upload-content-area
        ref="uploadArea"
        v-model="uploadType"
        class="upload-content-area"
        required
        :path="'essays'"
        :max-number-of-files="canBulkUpload ? 1000 : 1"
        :submitted="submitted"
        @input-files="files = $event"
        @input-text="essayContents = $event"
      >
        <template #description>
          <div v-if="!canBulkUpload">
            <eg-b-button
              variant="link"
              class="px-0 py-0 account-link"
              @click="trackAndRedirectToUpgrade('bulk_upload')"
            >
              {{ $t("essays.addSubmissions.upgradePro") }}
            </eg-b-button>
            {{ $t("essays.addSubmissions.toUploadMore") }}
          </div>
        </template>
      </upload-content-area>
    </div>

    <imports-select-submissions-table
      v-else
      v-model="selectedIds"
      :submissions="lmsSubmissions"
      :loading="loading"
      :style="{ minWidth: '780px' }"
      :show-open-in-lms="isGoogleClassroomFolder"
      @essay-clicked="onEssayClicked"
    />

    <template #footer="{ close }">
      <eg-button type="secondary" @click="close">{{
        $t("essays.addSubmissions.close")
      }}</eg-button>
      <eg-button
        type="primary"
        :is-loading="loading"
        :disabled="!isEssayGraderFolder && selectedIds.length === 0"
        :loading-text="$t('common.processing')"
        @click="onAddSubmissions"
      >
        {{ $t("essays.addSubmissions.title") }}
      </eg-button>
    </template>

    <lms-import-errors-modal
      v-if="showErrorsModal"
      :submissions="failedSubmissions"
      :loading="false"
      @close="showErrorsModal = false"
    />
  </eg-modal>
</template>

<script>
// TODO: Add separate component for upload area then use here and in essay_submission_form.vue

import {
  feedback,
  getFolderLmsSubmissions,
  setFolderLmsSubmissions,
} from "@/api";
import UploadContentArea from "@/components/dashboard/upload_content_area.vue";
import ImportsSelectSubmissionsTable from "@/components/imports/imports_select_submissions_table.vue";
import LmsImportErrorsModal from "@/components/essay/modals/lms_import_errors_modal.vue";
import { PLATFORMS } from "@/constants";

const getAdditionalPayload = ({ name, content, fileUrl }) => {
  const additionalPayload = {
    name,
    content: content || "",
  };

  if (fileUrl) {
    additionalPayload.fileUrl = fileUrl;
  }

  return additionalPayload;
};

export default {
  name: "AddSubmissionsModal",

  components: {
    UploadContentArea,
    ImportsSelectSubmissionsTable,
    LmsImportErrorsModal,
  },

  inject: ["workspacesProvider"],

  props: {
    workspaceKey: {
      type: String,
      default: null,
    },

    folderId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      uploadType: "files",
      files: [],
      essayContents: [],
      name: "",
      submitted: false,
      loading: false,
      lmsSubmissions: [],
      selectedIds: [],
      showErrorsModal: false,
      failedSubmissions: [],
    };
  },

  computed: {
    folder() {
      return this.workspacesProvider.getFolderById(
        this.workspaceKey,
        this.folderId
      );
    },
    isEssayGraderFolder() {
      return this.folder.platform === "essaygrader";
    },
    isGoogleClassroomFolder() {
      return [PLATFORMS.GOOGLE].includes(this.folder.platform);
    },
    canBulkUpload() {
      // TODO: Temporarily allow everyone to use bulk upload.
      // After 3.0 release, if we still allow all users to bulk upload, remove this code.
      return true;
      // return this.$store.getters.userIsNotPausedAndPlanMoreThenPro;
    },
  },

  watch: {
    isEssayGraderFolder: {
      handler() {
        if (!this.isEssayGraderFolder) {
          this.initLmsSubmissions();
        }
      },
      immediate: true,
    },
  },

  methods: {
    closeModal() {
      this.$egModal.hide("add-submissions-modal");
    },

    async initLmsSubmissions() {
      this.loading = true;
      const {
        data: { submissions },
      } = await getFolderLmsSubmissions(this.folderId);
      this.selectedIds = submissions
        .filter((submission) => submission.status === "NotImported")
        .map((submission) => submission.id);
      this.lmsSubmissions = submissions;
      this.loading = false;
    },

    onEssayClicked() {
      this.closeModal();
    },

    async onAddSubmissions() {
      if (!this.isEssayGraderFolder) {
        this.loading = true;
        const { data } = await setFolderLmsSubmissions(
          this.folderId,
          this.selectedIds
        ).finally(() => (this.loading = false));

        // Check if there are any failed imports
        const failedSubmissions = data.filter((item) => !item.imported);

        if (failedSubmissions.length > 0) {
          // Show modal with errors table
          this.failedSubmissions = failedSubmissions;
          this.showErrorsModal = true;
        } else {
          this.$showToastSuccess(
            this.$t("essays.addSubmissions.importSuccessful"),
            { title: this.$t("essays.addSubmissions.importSuccessful") }
          );
          this.$emit("submitted");
          this.closeModal();
        }

        return;
      }

      try {
        this.loading = true;
        const payload = {
          folderId: this.folderId,
        };

        if (this.uploadType === "files") {
          await this.$refs.uploadArea.upload();

          if (this.files.length === 0) {
            this.$showToastError(
              this.$t("essays.addSubmissions.uploadAtLeastOne")
            );
            this.loading = false;

            return;
          }

          const filesPayload = this.files.map((file) => {
            return getAdditionalPayload(file);
          });

          payload.files = filesPayload;
        } else if (this.essayContents.length > 0) {
          if (!this.name) {
            this.$showToastError(this.$t("essays.addSubmissions.nameRequired"));
            this.loading = false;

            return;
          }

          payload.files = [
            {
              name: this.name,
              content: this.essayContents,
            },
          ];
        }

        await feedback(payload);
        this.loading = false;
        this.$emit("submitted");
        this.closeModal();
      } catch (error) {
        this.loading = false;

        this.$showToastError(error?.response?.data?.error || error);

        return;
      }
    },
  },
};
</script>
